import { useState } from 'react'
import { Link } from 'react-router'
import logoUrl from '#app/assets/logo.png'
import { PricingModal } from '#app/components/pricing-modal.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { WaitlistModal } from '#app/components/waitlist-modal.tsx'
import {
	getFAQSchema,
	getOrganizationSchema,
	getSoftwareApplicationSchema,
	getWebPageSchema,
} from '#app/utils/structured-data.ts'
import { type Route } from './+types/index.ts'

export const meta: Route.MetaFunction = () => {
	const title = 'MyBillingsMethod - Pure Billings Method Tracker for Mac'
	const description =
		'Track the Billings Ovulation Method with complete privacy. No thermometer required. AI-powered pattern recognition. Your data never leaves your Mac. Beta launching April 2026.'

	return [
		{ title },
		{ name: 'description', content: description },
		{
			name: 'keywords',
			content:
				'Billings Method app, fertility tracking, cervical mucus tracking, natural family planning Mac, NFP app privacy, ovulation method app, fertility charting software, privacy-first fertility app',
		},

		// Open Graph
		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:type', content: 'website' },
		{ property: 'og:url', content: 'https://mybillingsmethod.com' },
		{
			property: 'og:image',
			content: 'https://mybillingsmethod.com/og-image.png',
		},

		// Twitter
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ name: 'twitter:title', content: title },
		{ name: 'twitter:description', content: description },
		{
			name: 'twitter:image',
			content: 'https://mybillingsmethod.com/twitter-image.png',
		},

		// Canonical
		{ tagName: 'link', rel: 'canonical', href: 'https://mybillingsmethod.com' },
	]
}

export default function LandingPage() {
	const [waitlistOpen, setWaitlistOpen] = useState(false)
	const [pricingOpen, setPricingOpen] = useState(false)

	const organizationSchema = getOrganizationSchema()
	const softwareSchema = getSoftwareApplicationSchema()
	const webPageSchema = getWebPageSchema(
		'MyBillingsMethod - Pure Billings Method Tracker for Mac',
		'Track the Billings Method with complete privacy. No cloud storage, AI insights, Mac-native.',
		'https://mybillingsmethod.com',
	)
	const faqSchema = getFAQSchema()

	return (
		<>
			{/* Structured Data for SEO */}
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
			/>
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(softwareSchema) }}
			/>
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
			/>
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
			/>

			<div className="bg-white">
				<Navigation onPricingClick={() => setPricingOpen(true)} />
				<HeroSection onWaitlistClick={() => setWaitlistOpen(true)} />
				<ValuePropsSection />
				<ComparisonSection />
				<PricingSection onWaitlistClick={() => setWaitlistOpen(true)} />
				<FinalCTA />
				<Footer />

				<WaitlistModal open={waitlistOpen} onOpenChange={setWaitlistOpen} />
				<PricingModal open={pricingOpen} onOpenChange={setPricingOpen} />
			</div>
		</>
	)
}

function Navigation({ onPricingClick }: { onPricingClick: () => void }) {
	return (
		<nav className="sticky top-0 z-40 border-b border-gray-100 bg-white/80 backdrop-blur-sm">
			<div className="mx-auto max-w-5xl px-6 py-4">
				<div className="flex items-center justify-between">
					<Link to="/" className="flex items-center gap-2">
						<img src={logoUrl} alt="" className="h-8 w-auto" />
						<span
							className="text-lg font-semibold text-gray-900"
							style={{ fontFamily: "'Outfit', sans-serif" }}
						>
							MyBillingsMethod
						</span>
					</Link>
					<div className="flex items-center gap-6">
						<Link
							to="/blog"
							className="text-sm font-medium text-gray-600 hover:text-gray-900"
						>
							Blog
						</Link>
						<button
							onClick={onPricingClick}
							className="text-sm font-medium text-gray-600 hover:text-gray-900"
						>
							Pricing
						</button>
					</div>
				</div>
			</div>
		</nav>
	)
}

function HeroSection({ onWaitlistClick }: { onWaitlistClick: () => void }) {
	return (
		<section className="bg-gradient-to-br from-purple-600 to-purple-800 text-white">
			<div className="mx-auto max-w-4xl px-6 py-32 text-center">
				{/* Trust Badge */}
				<div className="mb-8 inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-medium backdrop-blur-sm">
					<Icon name="lock-closed" className="mr-2 h-4 w-4" />
					100% Local • Data Never Leaves Your Mac
				</div>

				{/* Headline */}
				<h1 className="mb-6 text-5xl font-bold leading-tight md:text-6xl">
					Pure Billings Method
					<br />
					Tracker for Mac
				</h1>

				{/* Subheading */}
				<p className="mx-auto mb-12 max-w-2xl text-xl text-purple-100 md:text-2xl">
					No thermometer. No cloud. Just you and your chart.
					<br />
					AI that helps you learn, never overrides.
				</p>

				{/* CTAs */}
				<div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
					<Button asChild size="lg" className="w-full bg-white text-purple-600 hover:bg-purple-50 sm:w-auto shadow-lg">
						<Link to="/checkout/PRACTITIONER?beta=true">
							Reserve Beta Access - £29/year
						</Link>
					</Button>
					<Button
						onClick={onWaitlistClick}
						size="lg"
						variant="outline"
						className="w-full border-2 border-white bg-transparent text-white hover:bg-white/10 sm:w-auto"
					>
						Join Free Waitlist
					</Button>
				</div>

				{/* Launch Info */}
				<p className="mt-6 text-sm text-purple-200">
					Launching April 2026 • First 100 users lock in £29/year forever
				</p>
			</div>
		</section>
	)
}

function ValuePropsSection() {
	return (
		<section className="bg-gray-50 py-20">
			<div className="mx-auto max-w-5xl px-6">
				<div className="grid gap-12 md:grid-cols-3">
					{/* Point 1 */}
					<div className="text-center">
						<div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-purple-100">
							<Icon name="check" className="h-7 w-7 text-purple-600" />
						</div>
						<h3 className="mb-2 text-xl font-bold text-gray-900">
							Pure Billings Method
						</h3>
						<p className="text-gray-600">
							One biomarker. No devices. Follows what you learned in class; no
							algorithms second-guessing you.
						</p>
					</div>

					{/* Point 2 */}
					<div className="text-center">
						<div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-green-100">
							<Icon name="lock-closed" className="h-7 w-7 text-green-600" />
						</div>
						<h3 className="mb-2 text-xl font-bold text-gray-900">
							Actually Private
						</h3>
						<p className="text-gray-600">
							Your data physically cannot leave your Mac. No cloud. No tracking.
							Privacy by architecture.
						</p>
					</div>

					{/* Point 3 */}
					<div className="text-center">
						<div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-100">
							<Icon name="check" className="h-7 w-7 text-blue-600" />
						</div>
						<h3 className="mb-2 text-xl font-bold text-gray-900">
							AI That Helps You Learn
						</h3>
						<p className="text-gray-600">
							Local AI recognises your patterns and helps you understand them but
							never overrides the method.
						</p>
					</div>
				</div>
			</div>
		</section>
	)
}

function ComparisonSection() {
	return (
		<section className="bg-white py-20">
			<div className="mx-auto max-w-3xl px-6 text-center">
				<h2 className="mb-4 text-3xl font-bold text-gray-900">
					Why Billings? Why Local-First?
				</h2>
				<p className="mb-12 text-xl text-gray-600">
					Simple. One biomarker. No devices required.
				</p>

				<div className="grid gap-8 text-left md:grid-cols-2">
					{/* Billings Advantage */}
					<div className="rounded-xl bg-purple-50 p-8">
						<h3 className="mb-4 text-lg font-bold text-purple-900">
							The Billings Method:
						</h3>
						<ul className="space-y-3 text-gray-700">
							<li className="flex items-start">
								<Icon
									name="check"
									className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0 text-green-600"
								/>
								One biomarker: cervical mucus
							</li>
							<li className="flex items-start">
								<Icon
									name="check"
									className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0 text-green-600"
								/>
								No thermometer (save £20-100)
							</li>
							<li className="flex items-start">
								<Icon
									name="check"
									className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0 text-green-600"
								/>
								Works with irregular schedules
							</li>
							<li className="flex items-start">
								<Icon
									name="check"
									className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0 text-green-600"
								/>
								98-99% effective when practiced correctly
							</li>
						</ul>
					</div>

					{/* Privacy Advantage */}
					<div className="rounded-xl bg-green-50 p-8">
						<h3 className="mb-4 text-lg font-bold text-green-900">
							Local-first means:
						</h3>
						<ul className="space-y-3 text-gray-700">
							<li className="flex items-start">
								<Icon
									name="lock-closed"
									className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0 text-green-600"
								/>
								Data physically can't leave your Mac
							</li>
							<li className="flex items-start">
								<Icon
									name="lock-closed"
									className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0 text-green-600"
								/>
								No cloud servers to breach
							</li>
							<li className="flex items-start">
								<Icon
									name="lock-closed"
									className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0 text-green-600"
								/>
								Works completely offline
							</li>
							<li className="flex items-start">
								<Icon
									name="lock-closed"
									className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0 text-green-600"
								/>
								No trust required; privacy by design
							</li>
						</ul>
					</div>
				</div>
			</div>
		</section>
	)
}

function PricingSection({ onWaitlistClick }: { onWaitlistClick: () => void }) {
	return (
		<section className="bg-purple-50 py-20">
			<div className="mx-auto max-w-4xl px-6 text-center">
				<h2 className="mb-4 text-3xl font-bold text-gray-900">
					Simple Pricing
				</h2>
				<p className="mb-12 text-xl text-gray-600">
					Try free. Or lock in beta pricing before launch.
				</p>

				<div className="grid gap-6 md:grid-cols-3">
					{/* Free */}
					<div className="rounded-xl border-2 border-gray-200 bg-white p-8">
						<div className="mb-6">
							<h3 className="mb-2 text-2xl font-bold text-gray-900">
								Try It Free
							</h3>
							<p className="text-4xl font-bold text-gray-900">£0</p>
							<p className="text-sm text-gray-500">Forever</p>
						</div>
						<Button
							onClick={onWaitlistClick}
							variant="outline"
							size="lg"
							className="w-full"
						>
							Join Waitlist
						</Button>
						<p className="mt-4 text-sm text-gray-600">
							Basic charting when app launches
						</p>
					</div>

					{/* Practitioner - Highlighted */}
					<div className="relative transform rounded-xl border-2 border-purple-600 bg-purple-600 p-8 text-white shadow-xl md:scale-105">
						<div className="absolute -top-3 left-1/2 -translate-x-1/2 transform rounded-full bg-green-500 px-3 py-1 text-xs font-bold text-white">
							BEST VALUE
						</div>
						<div className="mb-6">
							<h3 className="mb-2 text-2xl font-bold">Practitioner</h3>
							<p className="text-4xl font-bold">£29</p>
							<p className="text-sm text-purple-200">/year • First 100 users only</p>
						</div>
						<Button
							asChild
							size="lg"
							className="w-full bg-white text-purple-600 hover:bg-purple-50"
						>
							<Link to="/checkout/PRACTITIONER?beta=true">
								Reserve Beta Access
							</Link>
						</Button>
						<p className="mt-4 text-sm text-purple-200">
							AI insights + instructor exports
						</p>
					</div>

					{/* Couple */}
					<div className="rounded-xl border-2 border-gray-200 bg-white p-8">
						<div className="mb-6">
							<h3 className="mb-2 text-2xl font-bold text-gray-900">Couple</h3>
							<p className="text-4xl font-bold text-gray-900">£59</p>
							<p className="text-sm text-gray-500">/year</p>
						</div>
						<Button asChild size="lg" className="w-full">
							<Link to="/checkout/COUPLE">Reserve Access</Link>
						</Button>
						<p className="mt-4 text-sm text-gray-600">
							Everything + partner features
						</p>
					</div>
				</div>

				<p className="mt-8 text-sm text-gray-600">
					Full refund available anytime before launch (April 2026)
				</p>
			</div>
		</section>
	)
}

function FinalCTA() {
	return (
		<section className="bg-gradient-to-br from-purple-600 to-purple-800 py-20 text-white">
			<div className="mx-auto max-w-3xl px-6 text-center">
				<h2 className="mb-4 text-4xl font-bold">
					Master the Billings Method with privacy you can trust
				</h2>
				<p className="mb-8 text-xl text-purple-100">
					Launching April 2026 • Limited to 100 beta users
				</p>
				<Button
					asChild
					size="lg"
					className="bg-white text-purple-600 shadow-lg hover:bg-purple-50"
				>
					<Link to="/checkout/PRACTITIONER?beta=true">
						Reserve Your Spot - £29/year
					</Link>
				</Button>
			</div>
		</section>
	)
}

function Footer() {
	return (
		<footer className="border-t border-gray-200 bg-white py-12">
			<div className="mx-auto max-w-5xl px-6">
				<div className="flex flex-col items-center justify-between gap-4 text-sm text-gray-600 md:flex-row">
					<Link to="/" className="flex items-center gap-2">
						<img src={logoUrl} alt="" className="h-6 w-auto" />
						<span
							className="font-semibold text-gray-900"
							style={{ fontFamily: "'Outfit', sans-serif" }}
						>
							MyBillingsMethod
						</span>
					</Link>
					<div className="flex gap-6">
						<Link to="/blog" className="hover:text-gray-900">
							Blog
						</Link>
						<Link to="/privacy" className="hover:text-gray-900">
							Privacy
						</Link>
						<Link to="/tos" className="hover:text-gray-900">
							Terms
						</Link>
						<Link to="/support" className="hover:text-gray-900">
							Support
						</Link>
					</div>
				</div>
				<p className="mt-6 text-center text-xs text-gray-500">
					© {new Date().getFullYear()} MyBillingsMethod. Your data is yours. Always local. Always
					private.
				</p>
			</div>
		</footer>
	)
}
